import React, { useRef, useState } from 'react';
import type { ReceiptView } from 'shared-models/browser';
import { Menu, Modal, UnstyledButton, Loader, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircleCheck, faPrint, faPen } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import { TransactionReceiptContent, paymentMethodLabel } from './TransactionReceiptContent';
import { useTransactionReceiptController } from './useTransactionReceiptController';
import { ReceiptPrintSelectionModal } from './ReceiptPrintSelectionModal';
import { SUMMARY_JOB_ID } from './receipt-split.util';
import { ReportPrinterPickModal } from '../../reports/ReportPrinterPickModal';
import classes from './transaction-receipt.module.scss';
import { noSelectionTextInputProps } from '../receipt-input.utils';

export type TransactionReceiptModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  orderId: number;
  paymentlineId?: number;
  // postPayment = Odoo-style success screen (any order type, full close); print = printer-picker fallback (partial/split payments).
  variant?: 'postPayment' | 'print' | 'pastReceipt';
  onNewOrder?: () => void;
  onHeadlessPrint?: (receipt: ReceiptView, printerId: number, selectedJobIds?: readonly string[]) => void;
};

// Summary: Post-payment קבלה modal; iPad shows the paper preview, phone is print-only.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-receipt
const TransactionReceiptModalComponent: React.FC<TransactionReceiptModalProps> = ({
  opened,
  onClose,
  tenantId,
  orderId,
  paymentlineId,
  variant = 'print',
  onNewOrder,
  onHeadlessPrint,
}) => {
  const isTablet = useMediaQuery('(min-width: 768px)');
  const isPostPayment = variant === 'postPayment';
  const isPastReceipt = variant === 'pastReceipt';
  const [receiptSelectionOpen, setReceiptSelectionOpen] = useState(false);
  const [printerPickOpen, setPrinterPickOpen] = useState(false);
  const pendingJobIdsRef = useRef<readonly string[] | undefined>(undefined);
  const handlePrintClose = () => {
    onClose();
    if (isPostPayment) {
      onNewOrder?.();
    }
  };
  const c = useTransactionReceiptController({
    opened,
    tenantId,
    orderId,
    paymentlineId,
    onClose: handlePrintClose,
    onHeadlessPrint,
  });
  const { receipt } = c;
  const hasSinglePastReceipt = isPastReceipt && c.printJobs.length === 1;
  const isMobilePostPayment = isPostPayment && !isTablet;
  const tipSuffix = c.tipAmount === 0
    ? ''
    : ` ${c.tipAmount > 0 ? '+' : '-'} ${formatMoney(Math.abs(c.tipAmount))} טיפ`;

  // Empty clears the name (DTO allows null); otherwise mirror the DTO minimum length.
  const trimmedName = c.nameDraft.trim();
  const guestNameValid = trimmedName.length === 0 || trimmedName.length >= 2;

  // Fixed/sole printer prints silently; multi-printer opens the shared pick modal.
  const requestPrint = (jobIds?: readonly string[]) => {
    if (c.needsPrinterChoice) {
      pendingJobIdsRef.current = jobIds;
      setPrinterPickOpen(true);
      return;
    }
    c.handlePrintClick(jobIds);
  };

  const handlePrinterPicked = (printerId: number) => {
    setPrinterPickOpen(false);
    c.selectPrinter(String(printerId));
    void c.handlePrint(String(printerId), pendingJobIdsRef.current);
    pendingJobIdsRef.current = undefined;
  };

  // The summary copy only exists for multi-payment orders; otherwise both choices print the same sheet.
  const hasPrintScopeChoice = c.printJobs.some((job) => job.id === SUMMARY_JOB_ID);
  const printScopeItems = (
    <>
      <Menu.Item data-testid="receipt-print-scope-all" onClick={() => requestPrint()}>כל ההדפסות</Menu.Item>
      <Menu.Item data-testid="receipt-print-scope-summary" onClick={() => requestPrint(c.summaryPrintJobIds)}>קבלה מסכמת</Menu.Item>
    </>
  );

  const offscreenCapture = receipt && (
    <div className={classes.printCaptureOffscreenRow}>
      {c.printJobs.map((job, index) => (
        <div
          key={index}
          className={classes.offscreenReceiptReady}
          ref={(el) => { c.offscreenRefs.current[index] = el; }}
        >
          <TransactionReceiptContent receipt={job.receipt} title={job.title} showPaymentDetails={job.showPaymentDetails} />
        </div>
      ))}
    </div>
  );

  const ReceiptSurface: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    if ((isPostPayment && isTablet) || isPastReceipt) {
      return (
        <section className={classes.postPaymentPage} aria-label={isPastReceipt ? 'קבלה' : 'סיום תשלום'}>
          {children}
        </section>
      );
    }

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        size={isTablet ? 'xl' : 'sm'}
        centered={!isTablet}
        yOffset={isTablet ? '2dvh' : undefined}
        radius="md"
        dir="rtl"
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        classNames={{ root: classes.modalRoot }}
      >
        {children}
      </Modal>
    );
  };

  if (isMobilePostPayment) {
    return (
      <Modal
        opened={opened}
        onClose={() => {
          onClose();
          onNewOrder?.();
        }}
        size="100%"
        centered
        radius="md"
        dir="rtl"
        withCloseButton={false}
        closeOnEscape={false}
        classNames={{ root: classes.modalRoot, content: classes.mobileEndOrderContent, body: classes.mobileEndOrderBody }}
      >
        {c.loadingReceipt || !receipt ? (
          <div className={classes.statusArea}>
            <Loader size="sm" />
          </div>
        ) : (
          <div className={classes.mobileEndOrderModal} data-testid="receipt-mobile-end-order">
            <div className={classes.mobileEndOrderTotals}>
              <div className={classes.mobileEndOrderTotalRow}>
                <strong>סה״כ חשבון (כולל טיפ)</strong>
                <strong>{formatMoney(receipt.totals.billableTotal + receipt.totals.tipAmount)} ₪</strong>
              </div>
              <div className={classes.mobileEndOrderTotalRow}>
                <span>מכירות</span>
                <span>{formatMoney(receipt.totals.billableTotal)} ₪</span>
              </div>
              <div className={classes.mobileEndOrderTotalRow}>
                <span>טיפ</span>
                <span className={classes.mobileEndOrderTip}>{formatMoney(receipt.totals.tipAmount)} ₪</span>
              </div>
            </div>

            <div className={classes.mobileEndOrderPaid}>
              <span>שולם (ללא עודף)</span>
              <strong>{formatMoney(receipt.totals.paidAmount - receipt.totals.changeAmount)} ₪</strong>
            </div>

            <div className={classes.mobileEndOrderChange}>
              <img className={classes.mobileEndOrderChangeImage} src="/money.png" alt="" />
              <span>עודף</span>
              <strong>{formatMoney(receipt.totals.changeAmount)} ₪</strong>
              <FontAwesomeIcon icon={faCircleCheck as IconProp} className={classes.mobileEndOrderCheck} aria-hidden="true" />
            </div>

            <div className={classes.mobileEndOrderActions}>
              {(() => {
                const printButton = (
                  <UnstyledButton
                    component="button"
                    type="button"
                    className={classes.mobileEndOrderPrint}
                    data-testid="receipt-mobile-end-order-print"
                    onClick={hasPrintScopeChoice ? undefined : () => requestPrint()}
                    disabled={c.isPrinting || c.loadingPrinters || c.printers.length === 0}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                    <div> <span>{c.isPrinting ? 'מדפיס...' : 'הדפס חשבונית'}</span> </div>
                  </UnstyledButton>
                );
                return hasPrintScopeChoice ? (
                  <Menu position="top" withinPortal shadow="md" width={220}>
                    <Menu.Target>{printButton}</Menu.Target>
                    <Menu.Dropdown className={classes.printScopeMenu}>{printScopeItems}</Menu.Dropdown>
                  </Menu>
                ) : printButton;
              })()}
              <UnstyledButton
                component="button"
                type="button"
                className={classes.mobileEndOrderClose}
                data-testid="receipt-mobile-end-order-close"
                onClick={() => {
                  onClose();
                  onNewOrder?.();
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="9" x2="15" y2="15" /><line x1="15" y1="9" x2="9" y2="15" /></svg>
                <span>סגור הזמנה</span>
              </UnstyledButton>
            </div>
          </div>
        )}
        {offscreenCapture}
        <ReportPrinterPickModal
          opened={printerPickOpen}
          printers={c.printerChoices}
          disabled={c.isPrinting}
          onConfirm={handlePrinterPicked}
          onClose={() => setPrinterPickOpen(false)}
        />
      </Modal>
    );
  }

  if (isPostPayment || isPastReceipt) {
    return (
      <ReceiptSurface>
        {c.loadingReceipt || !receipt ? (
          <div className={classes.statusArea} data-testid="receipt-modal">
            <Loader size="sm" />
          </div>
        ) : (
          <div className={classes.postPaymentShell} data-testid="receipt-modal">
            <div className={classes.successPanel}>
              <div className={`${classes.successBanner}${isPastReceipt ? ` ${classes.closedBanner}` : ''}`}>
                {!isPastReceipt && (
                  <span className={classes.successCheck}>
                    <FontAwesomeIcon icon={faCircleCheck as IconProp} />
                  </span>
                )}
                <span className={`${classes.successTitle}${isPastReceipt ? ` ${classes.closedTitle}` : ''}`}>
                  {isPastReceipt ? 'הזמנה סגורה' : 'התשלום בוצע בהצלחה'}
                </span>
                <span className={classes.successAmount}>
                  {isPastReceipt
                    ? `${formatMoney(receipt.totals.paidAmount)} ₪`
                    : `${formatMoney(c.baseAmount)} ₪${tipSuffix}`}
                </span>
                {!isPastReceipt && receipt.totals.changeAmount > 0 && (
                  <span className={classes.successChange}>
                    עודף: {formatMoney(receipt.totals.changeAmount)} ₪
                  </span>
                )}
              </div>

              <div className={classes.panelActionsRow}>
                {isPastReceipt && !hasSinglePastReceipt ? (
                  <UnstyledButton
                    component="button"
                    type="button"
                    className={classes.panelActionButton}
                    data-testid="receipt-print-button"
                    onClick={() => setReceiptSelectionOpen(true)}
                    disabled={c.isPrinting || c.loadingPrinters || c.printers.length === 0}
                  >
                    <FontAwesomeIcon icon={faPrint as IconProp} />
                    {c.isPrinting ? 'מדפיס...' : 'בחר קבלות להדפסה'}
                  </UnstyledButton>
                ) : (
                  (() => {
                    const printButton = (
                      <UnstyledButton
                        component="button"
                        type="button"
                        className={classes.panelActionButton}
                        data-testid="receipt-print-button"
                        onClick={hasPrintScopeChoice ? undefined : () => requestPrint()}
                        disabled={c.isPrinting || c.loadingPrinters || c.printers.length === 0}
                      >
                        <FontAwesomeIcon icon={faPrint as IconProp} />
                        {c.isPrinting ? 'מדפיס...' : 'הדפס קבלה מלאה'}
                      </UnstyledButton>
                    );
                    return hasPrintScopeChoice ? (
                      <Menu position="bottom" withinPortal shadow="md" width={220}>
                        <Menu.Target>{printButton}</Menu.Target>
                        <Menu.Dropdown className={classes.printScopeMenu}>{printScopeItems}</Menu.Dropdown>
                      </Menu>
                    ) : printButton;
                  })()
                )}

                {!c.editingName && (
                  <UnstyledButton
                    component="button"
                    type="button"
                    className={classes.panelActionButton}
                    data-testid="receipt-edit-name-button"
                    onClick={c.startEditName}
                  >
                    <FontAwesomeIcon icon={faPen as IconProp} />
                    ערוך שם לקוח
                  </UnstyledButton>
                )}
              </div>

              {!c.loadingPrinters && c.printers.length === 0 && (
                <span className={classes.printerPickerEmpty}>אין מדפסת פעילה</span>
              )}

              {c.editingName && (
                <div className={classes.nameEditRow}>
                  <TextInput
                    value={c.nameDraft}
                    onChange={(event) => c.setNameDraft(event.currentTarget.value)}
                    placeholder="שם לקוח"
                    radius="md"
                    autoFocus
                    maxLength={35}
                    aria-label="שם לקוח"
                    style={{ flex: 1 }}
                    classNames={{ input: classes.guestNameInput }}
                    data-testid="receipt-guest-name-input"
                    {...noSelectionTextInputProps}
                  />
                  <Button variant="confirm" onClick={c.handleSaveName} disabled={c.savingName || !guestNameValid} dataTestId="receipt-guest-name-save">
                    {c.savingName ? 'שומר...' : 'שמור'}
                  </Button>
                  <Button variant="cancel" onClick={() => c.setEditingName(false)} disabled={c.savingName} dataTestId="receipt-guest-name-cancel">
                    ביטול
                  </Button>
                </div>
              )}

              {!c.editingName && (
                <Button
                  variant="confirm"
                  size="lg"
                  dataTestId="receipt-new-order-button"
                  onClick={() => {
                    onClose();
                    if (!isPastReceipt) {
                      onNewOrder?.();
                    }
                  }}
                >
                  {isPastReceipt ? 'סגור' : 'הזמנה חדשה'}
                </Button>
              )}
            </div>

            <div className={classes.receiptPaper}>
              <div className={classes.receiptScrollArea}>
                <TransactionReceiptContent receipt={receipt} />
              </div>
            </div>
          </div>
        )}

        {offscreenCapture}
        <ReceiptPrintSelectionModal
          opened={receiptSelectionOpen}
          jobs={c.printJobs.filter((job) => job.id !== SUMMARY_JOB_ID)}
          isPrinting={c.isPrinting}
          onClose={() => setReceiptSelectionOpen(false)}
          onPrint={(jobIds) => {
            setReceiptSelectionOpen(false);
            requestPrint(jobIds);
          }}
        />
        {printerPickOpen && (
          <ReportPrinterPickModal
            opened
            printers={c.printerChoices}
            disabled={c.isPrinting}
            onConfirm={handlePrinterPicked}
            onClose={() => setPrinterPickOpen(false)}
          />
        )}
      </ReceiptSurface>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="sm"
      centered
      radius="md"
      dir="rtl"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      classNames={{ root: classes.modalRoot }}
    >
      <div className={classes.shell} data-testid="receipt-print-modal">
        {c.loadingReceipt || !receipt ? (
          <div className={classes.statusArea}>
            <Loader size="sm" />
          </div>
        ) : (
          <>
            {isTablet ? (
              <div className={classes.receiptPaper}>
                <div className={classes.receiptScrollArea}>
                  <TransactionReceiptContent receipt={receipt} />
                </div>
              </div>
            ) : (
              <div className={classes.phoneSummary}>
                <div className={classes.phoneSummaryTitle}>חשבונית מס/קבלה</div>
                {receipt.payments.map((payment) => (
                  <div key={payment.paymentlineId} className={classes.phoneSummaryRow}>
                    <span>{paymentMethodLabel(payment)}</span>
                    <span>{formatMoney(payment.amount)} ₪</span>
                  </div>
                ))}
                {receipt.totals.discountAmount > 0 && (
                  <div className={classes.phoneSummaryRow}>
                    <span>הנחה</span>
                    <span>-{formatMoney(receipt.totals.discountAmount)} ₪</span>
                  </div>
                )}
                {receipt.totals.tipAmount !== 0 && (
                  <div className={classes.phoneSummaryRow}>
                    <span>טיפ</span>
                    <span>{formatMoney(receipt.totals.tipAmount)} ₪</span>
                  </div>
                )}
                <div className={classes.phoneSummaryTotal}>
                  <span>{receipt.totals.changeAmount > 0 ? 'סה״כ התקבל' : 'סה״כ שולם'}</span>
                  <span>{formatMoney(receipt.totals.paidAmount)} ₪</span>
                </div>
                {receipt.totals.changeAmount > 0 && (
                  <div className={classes.phoneSummaryRow}>
                    <span>עודף</span>
                    <span>{formatMoney(receipt.totals.changeAmount)} ₪</span>
                  </div>
                )}
              </div>
            )}

            <div className={classes.printerPickerSection}>
              <span className={classes.printerPickerLabel}>מדפסת</span>
              {c.loadingPrinters ? (
                <div className={classes.printerPickerLoading}><Loader size="xs" /></div>
              ) : c.printers.length === 0 ? (
                <span className={classes.printerPickerEmpty}>אין מדפסות פעילות</span>
              ) : (
                <div className={classes.printerPickerList}>
                  {c.printerChoices.map((printer) => {
                    const isSelected = c.selectedPrinterId === String(printer.id);
                    return (
                      <UnstyledButton
                        key={printer.id}
                        component="button"
                        type="button"
                        className={`${classes.printerPickerItem} ${isSelected ? classes.printerPickerItemActive : ''}`}
                        onClick={() => c.selectPrinter(String(printer.id))}
                        disabled={c.isPrinting}
                        data-testid={`receipt-printer-${printer.id}`}
                      >
                        <span className={classes.printerPickerItemName}>{printer.name}</span>
                        {isSelected && (
                          <span className={classes.printerPickerItemCheck}>
                            <FontAwesomeIcon icon={faCheck as IconProp} />
                          </span>
                        )}
                      </UnstyledButton>
                    );
                  })}
                </div>
              )}
            </div>

            <div className={classes.buttonRow}>
              <Button variant="cancel" fullWidth onClick={onClose} disabled={c.isPrinting} dataTestId="receipt-print-cancel">
                ביטול
              </Button>
              <Button
                variant="confirm"
                fullWidth
                onClick={() => { void c.handlePrint(); }}
                disabled={c.isPrinting || c.loadingPrinters || !c.selectedPrinterId}
                dataTestId="receipt-print-confirm"
              >
                {c.isPrinting ? 'מדפיס...' : 'הדפס'}
              </Button>
            </div>
          </>
        )}
      </div>

      {offscreenCapture}
    </Modal>
  );
};

export const TransactionReceiptModal = React.memo(TransactionReceiptModalComponent);
